import React, { useEffect, useRef, useState } from 'react';
import $ from 'jquery';
import { Icon } from '@iconify/react';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import { useValidator } from '../utils/useValidator';
import {
  createEventDelayCode,
  deleteEventDelayCode,
  getEventDelayCodes,
  updateEventDelayCode,
} from '../api/event-delay-codeAPI';

const sortCodes = (items) =>
  [...items].sort((a, b) => (a.code ?? '').localeCompare(b.code ?? '', undefined, { sensitivity: 'base' }));

const EventDelayCodesLayer = () => {
  const userProfile = useUserProfile();
  const [delayCodes, setDelayCodes] = useState([]);
  const [formState, setFormState] = useState({ code: '', name: '', type: '' });
  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);

  const tableRef = useRef(null);
  const dtRef = useRef(null);

  const validationFields = [
    { field: 'code', label: 'Code', required: true },
    { field: 'name', label: 'Name', required: true },
    { field: 'type', label: 'Type', required: true },
  ];
  const { errors, setErrors, validate } = useValidator(validationFields, formState);

  const resetDataTable = (fullyDestroy = false) => {
    if (dtRef.current) {
      dtRef.current.destroy(fullyDestroy);
      dtRef.current = null;
    }
  };

  const fetchDelayCodes = async () => {
    try {
      const data = await getEventDelayCodes();
      setDelayCodes(sortCodes(data || []));
    } catch (error) {
      console.error('Error loading event delay codes', error);
    }
  };

  useEffect(() => {
    fetchDelayCodes();
  }, []);

  useEffect(() => {
    resetDataTable();

    if (delayCodes.length) {
      dtRef.current = $(tableRef.current).DataTable({
        pageLength: 25,
        dom: 'rtp',
        responsive: false,
        language: { paginate: { first: '', last: '', next: '', previous: '' } },
      });
    }

    return () => resetDataTable(true);
  }, [delayCodes]);

  useEffect(() => {
    if (dtRef.current) {
      dtRef.current.search(searchTerm).draw();
    }
  }, [searchTerm]);

  const resetForm = () => {
    setFormState({ code: '', name: '', type: '' });
    setEditingId(null);
    setErrors({});
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
  };

  const handleEdit = (delayCode) => {
    setFormState({ code: delayCode.code || '', name: delayCode.name || '', type: delayCode.type || '' });
    setEditingId(delayCode.id);
    setShowModal(true);
  };

  const hasDuplicate = (fieldName) => {
    const value = formState[fieldName]?.trim().toLowerCase();
    if (!value) return false;

    return delayCodes.some(
      (item) => item.id !== editingId && item[fieldName]?.trim().toLowerCase() === value,
    );
  };

  const handleSave = async () => {
    const isValid = validate();
    const duplicateErrors = {};

    if (hasDuplicate('code')) {
      duplicateErrors.code = 'Code must be unique';
    }

    if (hasDuplicate('name')) {
      duplicateErrors.name = 'Name must be unique';
    }

    if (Object.keys(duplicateErrors).length) {
      setErrors((prev) => ({ ...prev, ...duplicateErrors }));
      return;
    }

    if (!isValid) return;

    const payload = {
      code: formState.code?.trim(),
      name: formState.name?.trim(),
      type: formState.type?.trim(),
    };

    if (editingId && userProfile?.id) {
      payload.updatedBy = userProfile.id;
    } else if (userProfile?.id) {
      payload.createdBy = userProfile.id;
    }

    try {
      const savedCode = editingId
        ? await updateEventDelayCode(editingId, payload)
        : await createEventDelayCode(payload);

      setShowModal(false);
      resetForm();
      resetDataTable();

      if (savedCode) {
        setDelayCodes((prev) => {
          if (editingId) {
            return sortCodes(prev.map((code) => (code.id === savedCode.id ? savedCode : code)));
          }
          return sortCodes([...prev, savedCode]);
        });
      } else {
        fetchDelayCodes();
      }
    } catch (error) {
      console.error('Error saving event delay code', error);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this event delay code?')) return;

    try {
      await deleteEventDelayCode(id);
      resetDataTable();
      setDelayCodes((prev) => prev.filter((code) => code.id !== id));
    } catch (error) {
      console.error('Error deleting event delay code', error);
    }
  };

  const canCreate = userProfile?.privileges?.includes('event-delay-codes:create');
  const canUpdate = userProfile?.privileges?.includes('event-delay-codes:update');
  const canDelete = userProfile?.privileges?.includes('event-delay-codes:delete');

  return (
    <div className='card h-100 p-0 rounded-0'>
      <div className='card-header d-flex justify-content-between align-items-center p-3'>
        <input
          type='text'
          className='form-control w-auto'
          placeholder='Search...'
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
        {canCreate && (
          <button
            className='btn btn-primary text-sm btn-sm px-16 py-12 radius-4 d-flex align-items-center gap-2'
            onClick={() => {
              resetForm();
              setShowModal(true);
            }}
          >
            <Icon icon='ic:baseline-plus' className='icon text-xl' />
            Add Event Delay Codes
          </button>
        )}
      </div>

      <div className='card-body p-0'>
        <div className='table-responsive scroll-sm'>
          <table className='table bordered-table mb-0 w-100 rounded-0' ref={tableRef}>
            <thead>
              <tr>
                <th>Code</th>
                <th>Name</th>
                <th>Type</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {delayCodes.length === 0 && (
                <tr>
                  <td colSpan='4' className='text-center py-4 rounded-0'>No Event Delay Codes Setup</td>
                </tr>
              )}
              {delayCodes.map((delayCode) => (
                <tr key={delayCode.id}>
                  <td><strong>{delayCode.code}</strong></td>
                  <td>{delayCode.name}</td>
                  <td>{delayCode.type}</td>
                  <td>
                    <div className='d-flex gap-2'>
                      {canUpdate && (
                        <button
                          className='w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center'
                          onClick={() => handleEdit(delayCode)}
                        >
                          <Icon icon='ic:baseline-edit' className='icon text-xl line-height-1' />
                        </button>
                      )}
                      {canDelete && (
                        <button
                          className='w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center'
                          onClick={() => handleDelete(delayCode.id)}
                        >
                          <Icon icon='ic:baseline-delete-outline' className='icon text-xl line-height-1' />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showModal && (
        <div className='modal show d-block' tabIndex='-1'>
          <div className='modal-dialog'>
            <div className='modal-content'>
              <div className='modal-header'>
                <h5 className='modal-title'>{editingId ? 'Edit Event Delay Code' : 'New Event Delay Code'}</h5>
                <button
                  type='button'
                  className='btn-close'
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                />
              </div>
              <div className='modal-body'>
                <div className='mb-3'>
                  <label className='form-label'>Code *</label>
                  <input
                    type='text'
                    className={`form-control ${errors.code ? 'is-invalid' : ''}`}
                    name='code'
                    value={formState.code}
                    onChange={handleInputChange}
                  />
                  {errors.code && <div className='invalid-feedback'>{errors.code}</div>}
                </div>
                <div className='mb-3'>
                  <label className='form-label'>Name *</label>
                  <input
                    type='text'
                    className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                    name='name'
                    value={formState.name}
                    onChange={handleInputChange}
                  />
                  {errors.name && <div className='invalid-feedback'>{errors.name}</div>}
                </div>
                <div className='mb-3'>
                  <label className='form-label'>Type *</label>
                  <input
                    type='text'
                    className={`form-control ${errors.type ? 'is-invalid' : ''}`}
                    name='type'
                    value={formState.type}
                    onChange={handleInputChange}
                  />
                  {errors.type && <div className='invalid-feedback'>{errors.type}</div>}
                </div>
              </div>
              <div className='modal-footer'>
                <button
                  className='btn btn-secondary'
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                >
                  Close
                </button>
                <button className='btn btn-primary' onClick={handleSave}>Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDelayCodesLayer;
